// UPPDRAG-38 — pure pricing calculator for leagues. NO supabase imports here
// so vitest can import + assert without booting the supabase client.
//
// The async loader (in ./leaguePricing.ts) does the supabase lookups and
// then delegates to calculateLeaguePriceForPlayer below.

// --- Types -------------------------------------------------------------------

/** A single row from public.league_membership_pricing. */
export interface LeagueMembershipPricingRule {
  membership_plan_id: string;
  discount_percent: number;
  is_free: boolean;
}

/** Result of calculateLeaguePriceForPlayer — kr units (matches existing UI). */
export interface LeaguePriceResult {
  /** Final price in kronor for the player after applying the best matching tier. */
  finalPriceKr: number;
  /** Final price in öre for the player. Convenience for Phase-4-aware downstream. */
  finalPriceMinor: number;
  /** Base price in kronor for the league (leagues.cost_per_player). */
  basePriceKr: number;
  /** Discount % applied (0 = no discount, 100 = free). null = no membership match. */
  appliedDiscountPercent: number | null;
  /** Whether the matched rule was `is_free=true`. */
  isFree: boolean;
  /** Membership plan id that was applied, if any. */
  appliedPlanId: string | null;
  /** Human-readable Swedish display string for the price line. */
  displayString: string;
}

export interface PricingInputs {
  basePriceKr: number;
  /** Player's active membership plan ids in this organization. Empty = no membership. */
  playerActivePlanIds: string[];
  /** All league_membership_pricing rules for this league (all tiers). */
  rules: LeagueMembershipPricingRule[];
  /** Optional human-readable plan name for the display string. */
  planNameById?: Record<string, string>;
}

// --- Pure calculator ---------------------------------------------------------

/**
 * Calculate the player's final league price given base + tier rules + memberships.
 *
 * Selection rules:
 *   - If player has multiple active memberships, pick the one with the BEST
 *     (lowest) effective price for this league. is_free always wins ties.
 *   - If no membership matches a rule, apply no discount (full base price).
 *   - discount_percent = 100 results in zero price even when is_free=false
 *     (defensive — both spellings of "free" produce the same outcome).
 *
 * Pure function: identical inputs → identical output. No I/O.
 */
export function calculateLeaguePriceForPlayer(input: PricingInputs): LeaguePriceResult {
  const { basePriceKr, playerActivePlanIds, rules, planNameById } = input;

  const ruleByPlanId = new Map(rules.map((r) => [r.membership_plan_id, r]));

  // No membership → full price.
  if (playerActivePlanIds.length === 0) {
    return {
      finalPriceKr: basePriceKr,
      finalPriceMinor: Math.round(basePriceKr * 100),
      basePriceKr,
      appliedDiscountPercent: null,
      isFree: basePriceKr === 0,
      appliedPlanId: null,
      displayString: basePriceKr === 0
        ? "Gratis"
        : `${basePriceKr} kr / spelare`,
    };
  }

  // Pick the lowest-effective-price plan among the player's active ones.
  let best: { planId: string; rule: LeagueMembershipPricingRule | null; priceKr: number } | null = null;

  for (const planId of playerActivePlanIds) {
    const rule = ruleByPlanId.get(planId) ?? null;
    let priceKr: number;
    if (rule?.is_free || rule?.discount_percent === 100) {
      priceKr = 0;
    } else if (rule) {
      const factor = (100 - rule.discount_percent) / 100;
      priceKr = Math.round(basePriceKr * factor);
    } else {
      priceKr = basePriceKr;
    }
    if (best === null || priceKr < best.priceKr) {
      best = { planId, rule, priceKr };
    }
  }

  const { planId, rule, priceKr } = best!;
  const isFree = priceKr === 0;
  const appliedDiscountPercent = rule
    ? (rule.is_free ? 100 : rule.discount_percent)
    : null;
  const planName = planNameById?.[planId];

  let displayString: string;
  if (rule && (rule.is_free || rule.discount_percent > 0)) {
    const baseLabel = basePriceKr === 0 ? "Gratis" : `${basePriceKr} kr`;
    const tierLabel = isFree
      ? "Gratis"
      : `${priceKr} kr (${appliedDiscountPercent}% rabatt)`;
    const memberPrefix = planName ? `Som ${planName}-medlem: ` : "Medlem: ";
    displayString = `Pris: ${baseLabel} | ${memberPrefix}${tierLabel}`;
  } else if (basePriceKr === 0) {
    displayString = "Gratis";
  } else {
    displayString = `${basePriceKr} kr / spelare`;
  }

  return {
    finalPriceKr: priceKr,
    finalPriceMinor: priceKr * 100,
    basePriceKr,
    appliedDiscountPercent,
    isFree,
    appliedPlanId: rule ? planId : null,
    displayString,
  };
}
